import { useState, useEffect } from "react";
import { Phone, Calendar, Menu, X } from "lucide-react";
import { CLINIC } from "@/data";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Meet Winston", href: "#winston" },
  { label: "Services", href: "#services" },
  { label: "Free Kids Dental", href: "#kids" },
  { label: "Smile Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-pd-bg/85 backdrop-blur-xl shadow-[0_4px_30px_rgba(44,64,46,0.07)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        <a href="#home" data-testid="logo-link" className="flex items-center shrink-0">
          <img
            src={scrolled ? "/pd-logo.png" : "/pd-logo-white.png"}
            alt="Proactive Dental"
            className="h-9 md:h-10 w-auto transition-opacity duration-300"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium whitespace-nowrap hover:text-pd-accent transition-colors duration-300 relative group ${scrolled ? "text-pd-body" : "text-white/90"}`}
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-pd-terra transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${CLINIC.phoneRaw}`}
            data-testid="header-call-btn"
            className={`flex items-center gap-2 text-sm font-medium hover:text-pd-accent transition-colors ${scrolled ? "text-pd-forest" : "text-white"}`}
          >
            <Phone size={16} /> {CLINIC.phoneDisplay}
          </a>
          <a
            href="#contact"
            data-testid="header-book-btn"
            className="flex items-center gap-2 bg-pd-terra hover:bg-pd-terradark text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-pd-terra/30"
          >
            <Calendar size={16} /> Book Appointment
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-1 transition-colors duration-300 ${scrolled ? "text-pd-forest" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mt-3 mx-4 rounded-2xl bg-pd-bg/95 backdrop-blur-xl shadow-xl p-6 flex flex-col gap-4">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-pd-body hover:text-pd-accent"
            >
              {n.label}
            </a>
          ))}
          <a href={`tel:${CLINIC.phoneRaw}`} className="flex items-center gap-2 text-pd-forest font-medium pt-2 border-t border-pd-sage/20">
            <Phone size={16} /> {CLINIC.phoneDisplay}
          </a>
          <a href="#contact" onClick={() => setOpen(false)} className="bg-pd-terra text-white rounded-full px-5 py-3 text-center font-medium">
            Book Appointment
          </a>
        </div>
      )}
    </header>
  );
}
